import React from 'react';
import { Badge } from '../Badge/Badge';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { Image } from '../Image/Image';
import { Video } from '../Video/Video';
import { PrimaryNavGroup } from './MegaMenu';

/** Props for the {@link MobileAccordion} expandable nav groups. */
export interface MobileAccordionProps {
  /** L1 navigation groups to render as accordion rows. */
  primaryNavGroups: PrimaryNavGroup[];
  /** ID of the currently expanded group, or `''` if none. */
  selectedPrimaryNavGroupId: string;
  /** Toggle callback — expands the given group or collapses if already open. */
  toggleSelectedPrimaryNavGroupId: (id: string) => void;
}

/** Scroll the drawer so the expanded accordion row is visible, or back to top on collapse. */
function scrollAccordionRow(row: HTMLElement, scrollToTop: boolean) {
  const sc = row.closest('.poly-mega-menu__drawer-scroll') as HTMLElement | null;
  if (!sc) return;
  const content = row.querySelector('.poly-mega-menu__accordion-content') as HTMLElement | null;
  const doScroll = () => {
    sc.scrollTop = scrollToTop
      ? 0
      : row.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop;
  };
  if (content && !scrollToTop) {
    content.addEventListener('transitionend', doScroll, { once: true });
  } else {
    requestAnimationFrame(doScroll);
  }
}

/**
 * MobileAccordion — Expandable L1 nav groups for the mobile drawer.
 *
 * Memoized to avoid re-renders when unrelated drawer state changes
 * (e.g. language picker toggle, CTA hover). Only re-renders when
 * the nav groups or selected group changes.
 */
export const MobileAccordion = React.memo<MobileAccordionProps>(({
  primaryNavGroups,
  selectedPrimaryNavGroupId,
  toggleSelectedPrimaryNavGroupId,
}) => {
  if (!Array.isArray(primaryNavGroups) || primaryNavGroups.length === 0) return null;

  return (
    <div className="poly-mega-menu__accordion">
      {primaryNavGroups.map((navGroup) => {
        const isDirectLink = navGroup.isDirectLink ||
          (navGroup.secondaryNavGroups?.length === 0 && !!navGroup.directLinkUrl);

        if (isDirectLink) {
          return (
            <div key={navGroup.id} className="poly-mega-menu__accordion-row">
              <a
                href={navGroup.directLinkUrl}
                className="poly-mega-menu__accordion-title"
                data-cta-id={navGroup.directLinkCtaId}
              >
                <h3>{navGroup.title}</h3>
              </a>
            </div>
          );
        }

        const isExpanded = selectedPrimaryNavGroupId === navGroup.id;
        return (
          <div
            key={navGroup.id}
            className={`poly-mega-menu__accordion-row${isExpanded ? ' poly-mega-menu__accordion-row--expanded' : ''}`}
          >
            <button
              type="button"
              id={`accordion-btn-${navGroup.id}`}
              className="poly-mega-menu__accordion-title"
              aria-expanded={isExpanded}
              aria-controls={`accordion-content-${navGroup.id}`}
              onClick={(e) => {
                const row = e.currentTarget.closest('.poly-mega-menu__accordion-row') as HTMLElement | null;
                toggleSelectedPrimaryNavGroupId(navGroup.id);
                if (row) scrollAccordionRow(row, isExpanded);
              }}
            >
              <h3>{navGroup.title}</h3>
              <CustomIcon name="chevron-down" className="poly-mega-menu__accordion-chevron" aria-hidden="true" />
            </button>
            <section
              className="poly-mega-menu__accordion-content"
              id={`accordion-content-${navGroup.id}`}
              aria-labelledby={`accordion-btn-${navGroup.id}`}
            >
              <div>
                {navGroup.secondaryNavGroups?.map((group, gi) => (
                  <div key={`${group.title}-${gi}`} className={`poly-mega-menu__accordion-links${group.columnType === 'dual' ? ' poly-mega-menu__accordion-links--dual' : ''}`}>
                    {group.title && <div className="poly-mega-menu__accordion-group-title">{group.title}</div>}
                    {group.links.map((link, li) => (
                      <a key={link.id ?? `${link.url}-${li}`} href={link.url} className="poly-mega-menu__accordion-link" data-cta-id={link.id}>
                        {link.iconUrl && (
                          <Image
                            bare
                            url={link.iconUrl}
                            alt={link.iconAlt || ''}
                            className="poly-mega-menu__accordion-link-icon"
                            aria-hidden="true"
                          />
                        )}
                        <span className="poly-mega-menu__accordion-link-text">
                          <span className="poly-mega-menu__accordion-link-title">{link.title}</span>
                          {link.description && (
                            <span className="poly-mega-menu__accordion-link-description">{link.description}</span>
                          )}
                        </span>
                      </a>
                    ))}
                  </div>
                ))}

                {/* Featured card — shown at bottom of accordion when nav group has featured content */}
                {navGroup.featuredImageUrl && navGroup.featuredTitle && (
                  navGroup.featuredVideoUrl ? (
                    // Video variant: <article> + <Video> + body (Video is a button,
                    // so the card cannot be wrapped in a single <a>).
                    <article className="poly-mega-menu__accordion-featured poly-mega-menu__accordion-featured--video">
                      <div className="poly-mega-menu__accordion-featured-image">
                        <Video
                          posterUrl={navGroup.featuredImageUrl}
                          posterAlt={navGroup.featuredTitle}
                          videoUrl={navGroup.featuredVideoUrl}
                          playBehavior="lightbox"
                        />
                      </div>
                      <div className="poly-mega-menu__accordion-featured-body">
                        {navGroup.featuredBadgeText && (
                          <Badge size="sm" tone="dark" className="poly-mega-menu__accordion-featured-badge">
                            {navGroup.featuredBadgeText}
                          </Badge>
                        )}
                        <span className="poly-mega-menu__accordion-featured-title">{navGroup.featuredTitle}</span>
                        {navGroup.featuredDescription && (
                          <span className="poly-mega-menu__accordion-featured-description">{navGroup.featuredDescription}</span>
                        )}
                      </div>
                    </article>
                  ) : navGroup.featuredUrl && (
                    <a
                      href={navGroup.featuredUrl}
                      className="poly-mega-menu__accordion-featured"
                      data-cta-id={navGroup.featuredId || undefined}
                    >
                      <div className="poly-mega-menu__accordion-featured-image">
                        <Image
                          url={navGroup.featuredImageUrl}
                          alt=""
                          objectFit="cover"
                        />
                      </div>
                      <div className="poly-mega-menu__accordion-featured-body">
                        {navGroup.featuredBadgeText && (
                          <Badge size="sm" tone="dark" className="poly-mega-menu__accordion-featured-badge">
                            {navGroup.featuredBadgeText}
                          </Badge>
                        )}
                        <span className="poly-mega-menu__accordion-featured-title">{navGroup.featuredTitle}</span>
                        {navGroup.featuredDescription && (
                          <span className="poly-mega-menu__accordion-featured-description">{navGroup.featuredDescription}</span>
                        )}
                      </div>
                    </a>
                  )
                )}
              </div>
            </section>
          </div>
        );
      })}
    </div>
  );
});

MobileAccordion.displayName = 'MobileAccordion';
